import {
  ANALYST_PRICE_TARGETS,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_INFORMATION_ANCHOR,
  COMPANY_PAST_ANCHOR,
  LegacyAnchor,
  PAST_EARNINGS_REVENUE,
  VALUATION_PE_RATIO,
  VALUATION_PRICE_VS_FAIR_RATIO,
  VALUATION_RATIO_VS_PEERS,
} from '@/constants/anchors';
import type {
  GroupRisks,
  RiskStatus,
  SectionFragment,
  StatementRecord,
  SummaryRiskStatement,
} from './types';
import type {
  Statement,
  StatementName,
} from '@/hooks/useCompanyStatementsAPIFormatted/types';
import { COMPANY_SUMMARY_ANCHOR } from '@/constants/anchors';
import { REWARDS, RISKS, riskFragments } from './constants';
import {
  DIVIDEND_STATEMENTS_DEFAULT,
  DIVIDEND_STATEMENTS_FINANCIAL,
  FUTURE_STATEMENTS,
  HEALTH_STATEMENTS_DEFAULT,
  HEALTH_STATEMENTS_FINANCIAL,
  HEALTH_STATEMENTS_LOSS_MAKING,
  PAST_STATEMENTS,
  VALUATION_STATEMENTS,
} from '../../constants';
import type { StatementsMap } from '../Introduction/utils';

export const getRiskRewardFragments = (
  statementName: string,
  outcomeName: string
): SectionFragment => {
  if (
    typeof riskFragments[statementName] !== 'undefined' &&
    typeof riskFragments[statementName][outcomeName] !== 'undefined'
  )
    return riskFragments[statementName][outcomeName];

  switch (statementName) {
    //Risks
    case 'IsAbleToAchieveProfitability':
    case 'AreRevenueAndEarningsExpectedToGrow':
      return { fragment: COMPANY_FUTURE_ANCHOR, sectionName: 'Section 4.0' };
    case 'IsInAGoodFinancialPosition':
      return { fragment: COMPANY_HEALTH_ANCHOR, sectionName: 'Section 6.0' };
    case 'IsDividendSustainable':
      return {
        fragment: COMPANY_DIVIDEND_ANCHOR,
        sectionName: 'Section 7.0',
      };
    case 'HasStableSharePriceOverPast3Months':
    case 'HasNoConcerningRecentEvents':
      return {
        fragment: LegacyAnchor.HEADER_PRICE_EVENTS,
        sectionName: 'Section 2.1',
      };
    case 'HasSufficientFinancialData':
      return { fragment: COMPANY_PAST_ANCHOR, sectionName: 'Section 5.0' };
    case 'HasHighQualityEarnings':
    case 'HasNetProfitMarginImprovedOverPastYear':
    case 'HasMeaningfulRevenue':
      return {
        fragment: PAST_EARNINGS_REVENUE,
        sectionName: 'Section 5.1',
      };
    case 'HasNoSubstantialInsiderSellingOverPastQuarter':
      return {
        fragment: LegacyAnchor.OWNERSHIP_INSIDER_TRADING,
        sectionName: 'Section 9.1',
      };
    case 'HasNotDilutedOverPastYear': {
      return {
        fragment: LegacyAnchor.OWNERSHIP,
        sectionName: 'Section 9.3',
      };
    }
    case 'HasMeaningfulMarketCap':
      return {
        fragment: COMPANY_INFORMATION_ANCHOR,
        sectionName: 'Section 10.0',
      };
    case 'HasNoNegativeEquity':
      return { fragment: COMPANY_HEALTH_ANCHOR, sectionName: 'Section 6.2' };
    //Rewards
    case 'IsGoodValue':
      return outcomeName === 'OUTCOME_TRUE'
        ? { fragment: VALUATION_PRICE_VS_FAIR_RATIO, sectionName: '' }
        : { fragment: VALUATION_PE_RATIO, sectionName: '' };
    case 'IsGrowingProfitOrRevenue':
      return { fragment: COMPANY_FUTURE_ANCHOR, sectionName: '' };
    case 'HasBeenGrowingProfitOrRevenue':
      return { fragment: COMPANY_PAST_ANCHOR, sectionName: '' };
    case 'IsDividendAttractive':
      return { fragment: COMPANY_DIVIDEND_ANCHOR, sectionName: '' };
    case 'IsGoodRelativeValue':
      return { fragment: VALUATION_RATIO_VS_PEERS, sectionName: '' };
    case 'IsTradingBelowAnalystPriceTargets':
      return { fragment: ANALYST_PRICE_TARGETS, sectionName: '' };
    default:
      return { fragment: COMPANY_SUMMARY_ANCHOR, sectionName: '' };
  }
};

export const getPassRewardList = (statements: StatementRecord): Statement[] => {
  const statementList = Object.values(statements || []);
  return statementList
    .filter(
      (statement) =>
        REWARDS.includes(statement.name) && statement.value === true
    )
    .sort((a, b) => REWARDS.indexOf(a.name) - REWARDS.indexOf(b.name));
};

export const getRiskStatus = (risks: GroupRisks): RiskStatus => {
  const insufficientData =
    risks.failRisksMayor.length +
      risks.failRisksMinor.length +
      risks.passRisks.length ===
    0;

  const hasPassedAllRisks =
    risks.failRisksMayor.length +
      risks.failRisksMinor.length +
      risks.noDataRisks.length ===
      0 && risks.passRisks.length > 0;

  const hasNotFailedAnyRisk =
    risks.failRisksMayor.length + risks.failRisksMinor.length === 0;

  if (insufficientData) return 'insufficientData';
  if (hasPassedAllRisks) return 'hasPassedAllRisks';
  if (hasNotFailedAnyRisk) return 'hasNotFailedAnyRisk';
  return 'hasFailedRisks';
};

export const getFormattedDescription = (description: string | null) => {
  if (description === null) return null;
  return description.replace(/\[\[(.*?)\]\]/g, (_matched, original: string) => {
    return original;
  });
};

const getStatementRisks = (statements: Statement[]) =>
  statements.filter((statement) => RISKS.includes(statement?.name));

const addFragmentsToRisks = (risks: Statement[]): SummaryRiskStatement[] => {
  return risks.map((risk) => {
    const { fragment: hash, sectionName } = getRiskRewardFragments(
      risk.name,
      risk.outcome_name
    );
    return { ...risk, hash, sectionName };
  });
};

const initGroupList: GroupRisks = {
  failRisksMayor: [],
  failRisksMinor: [],
  passRisks: [],
  noDataRisks: [],
};

const groupRisks = (risks: SummaryRiskStatement[]) => {
  return risks.reduce((acc, risk) => {
    if (risk.value === false && risk.severity === 'major')
      return {
        ...acc,
        failRisksMayor: [...acc.failRisksMayor, risk],
      };

    if (risk.value === false && risk.severity === 'minor')
      return {
        ...acc,
        failRisksMinor: [...acc.failRisksMinor, risk],
      };

    if (risk.value === true)
      return { ...acc, passRisks: [...acc.passRisks, risk] };

    if (typeof risk.value !== 'boolean')
      return { ...acc, noDataRisks: [...acc.noDataRisks, risk] };

    return acc;
  }, initGroupList);
};

export const getRiskList = (statements: StatementRecord): GroupRisks => {
  const statementList = Object.values(statements || []);
  const riskList = getStatementRisks(statementList);
  const risksWithFragments = addFragmentsToRisks(riskList);
  return groupRisks(risksWithFragments);
};

const getHealthStatements = (
  isFinancialCompany: boolean,
  isLossMaking: boolean
) => {
  if (isFinancialCompany) {
    return HEALTH_STATEMENTS_FINANCIAL;
  }
  if (isLossMaking) {
    return HEALTH_STATEMENTS_LOSS_MAKING;
  }
  return HEALTH_STATEMENTS_DEFAULT;
};

const getDividendStatements = (isFinancialCompany: boolean) => {
  if (isFinancialCompany) {
    return DIVIDEND_STATEMENTS_FINANCIAL;
  }
  return DIVIDEND_STATEMENTS_DEFAULT;
};

const getChecks = (
  sectionStatements: StatementName[],
  statements: StatementsMap
) => {
  return sectionStatements.map((value) =>
    statements[value]?.value === true ? 'PASS' : 'FAIL'
  );
};

export enum SectionIndex {
  VALUATION,
  FUTURE,
  PAST,
  HEALTH,
  DIVIDEND,
}

export const getSectionChecks = (
  isFinancialCompany: boolean,
  isLossMaking: boolean,
  statements: StatementsMap
) => {
  const HEALTH_STATEMENTS = getHealthStatements(
    isFinancialCompany,
    isLossMaking
  );
  const DIVIDEND_STATEMENTS = getDividendStatements(isFinancialCompany);

  return {
    [SectionIndex.VALUATION]: getChecks(VALUATION_STATEMENTS, statements),
    [SectionIndex.FUTURE]: getChecks(FUTURE_STATEMENTS, statements),
    [SectionIndex.PAST]: getChecks(PAST_STATEMENTS, statements),
    [SectionIndex.HEALTH]: getChecks(HEALTH_STATEMENTS, statements),
    [SectionIndex.DIVIDEND]: getChecks(DIVIDEND_STATEMENTS, statements),
  };
};
